import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';

export interface MediaBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Returns the rendered box of a media element (img/video) in CSS pixels,
 * relative to its parent. Needed because `object-contain` letterboxes the
 * media inside its container — the SVG overlay must match the media's actual
 * rendered bounds, not the container.
 *
 * Uses layout (offset) measurements rather than `getBoundingClientRect` so
 * values remain transform-independent — required when an ancestor applies a
 * CSS transform (e.g. zoom on the image branch), otherwise the SVG would
 * receive already-scaled dimensions and double-scale under the transform.
 *
 * Updates on load, ResizeObserver, and window resize.
 */
export function useMediaBox(mediaRef: RefObject<HTMLImageElement | HTMLVideoElement | null>): {
  box: MediaBox | null;
  onLoad: () => void;
} {
  const [box, setBox] = useState<MediaBox | null>(null);

  const measure = useCallback(() => {
    const media = mediaRef.current;
    const parent = media?.parentElement;
    if (!media || !parent) return;
    const next: MediaBox = {
      left: media.offsetLeft,
      top: media.offsetTop,
      width: media.offsetWidth,
      height: media.offsetHeight,
    };
    setBox((prev) => (prev && boxEquals(prev, next) ? prev : next));
  }, [mediaRef]);

  useEffect(() => {
    const media = mediaRef.current;
    const parent = media?.parentElement;
    if (!media || !parent) return;

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(media);
    ro.observe(parent);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [mediaRef, measure]);

  return { box, onLoad: measure };
}

function boxEquals(a: MediaBox, b: MediaBox): boolean {
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
}
